import React, { useEffect, useMemo, useState } from "react";
import axios from "../../../api/axiosInstance";
import { readStoredUser } from "../../../utils/sessionUser";
import { useReceivingPopupUi } from "./receivingPopupMessages";
import "../../PurchasingUi.css";
import "./PurchasingReceivingPopups.css";

export default function BackordersCleanupPopup({ isOpen, onClose }) {
  const currentUser = readStoredUser();
  const canCancel = Number(currentUser?.id) === 1 || currentUser?.permissions?.["purchasing.orders.update"] === true;
  const { t, themeId } = useReceivingPopupUi();
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setSelected({});
    setConfirming(false);
    setError("");
    setNotice("");
    setChanged(false);
    setLoading(true);
    axios.get("/api/purchasing/dashboard/pending-purchase-items")
      .then(({ data }) => { if (!cancelled) setLines(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setError("Could not load pending order quantities."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen]);

  const selectedIds = useMemo(() => [...new Set(lines
    .filter((line) => selected[line.po_line_id])
    .map((line) => line.po_line_id)
    .filter(Boolean))], [lines, selected]);

  const cancelSelected = async () => {
    if (!canCancel || busy || !confirming || !selectedIds.length) return;
    setBusy(true);
    setError("");
    try {
      await axios.patch("/api/purchasing/orders/backorders/cancel", { line_ids: selectedIds });
      setLines((current) => current.filter((line) => !selectedIds.includes(line.po_line_id)));
      setSelected({});
      setConfirming(false);
      setChanged(true);
      setNotice("Pending quantities cancelled.");
      try {
        const { data } = await axios.get("/api/purchasing/dashboard/pending-purchase-items");
        setLines(Array.isArray(data) ? data : []);
      } catch (_refreshError) {
        setError("Quantities cancelled. The remaining list could not be reloaded; reopen this view to load it again.");
      }
    } catch (_error) {
      setError("Could not cancel pending quantities.");
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pr-backorders pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId}>
      <section className="pr-backorders-card" role="dialog" aria-modal="true" aria-labelledby="pr-backorders-title">
        <header className="pr-backorders-head">
          <div>
            <h2 id="pr-backorders-title">{t("Close pending order quantities")}</h2>
            <p>{t("Select the quantities that will no longer be delivered. Received stock is not changed.")}</p>
          </div>
          <button type="button" className="erp-btn" onClick={() => onClose(changed)} disabled={busy} aria-label={t("Close")}>×</button>
        </header>

        {error && <p className="pr-backorders-message" role="alert">{t(error)}</p>}
        {notice && <p className="pr-backorders-message" role="status">{t(notice)}</p>}
        {loading ? <p className="pr-backorders-message" role="status">{t("Loading...")}</p> : (
          <div className="pr-backorders-table">
            <table>
              <thead><tr>
                <th aria-label={t("Select")} />
                <th>{t("Order Date")}</th><th>{t("Order")}</th><th>{t("Supplier")}</th><th>{t("Item")}</th><th>{t("Pending")}</th>
              </tr></thead>
              <tbody>
                {lines.map((line) => (
                  <tr key={line.po_line_id} data-selected={Boolean(selected[line.po_line_id])}>
                    <td><input type="checkbox" checked={Boolean(selected[line.po_line_id])} disabled={!canCancel || busy || confirming || !line.po_line_id}
                      aria-label={t("Select line {code}", { code: `${line.order_code} · ${line.item}` })}
                      onChange={() => setSelected((current) => ({ ...current, [line.po_line_id]: !current[line.po_line_id] }))} /></td>
                    <td>{line.order_date || "—"}</td><td>{line.order_code}</td><td>{line.supplier}</td><td>{line.item}</td><td><strong>{line.qty_pending}</strong></td>
                  </tr>
                ))}
                {!lines.length && <tr><td colSpan="6">{t("No pending items")}</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {canCancel && confirming && (
          <section className="pr-backorders-confirm" role="alertdialog" aria-modal="true" aria-labelledby="pr-backorders-confirm-title">
            <p id="pr-backorders-confirm-title">{t("Cancel the outstanding quantities on {count} selected lines? This does not remove stock already received.", { count: selectedIds.length })}</p>
            <div className="pr-backorders-actions">
              <button type="button" className="erp-btn" onClick={() => setConfirming(false)} disabled={busy}>{t("Keep pending")}</button>
              <button type="button" className="erp-btn erp-btn--danger" onClick={cancelSelected} disabled={busy}>{t(busy ? "Working..." : "Confirm cancellation")}</button>
            </div>
          </section>
        )}
        <footer className="pr-backorders-foot">
          <span>{t("Selected pending lines: {count}", { count: selectedIds.length })}</span>
          <div className="pr-backorders-actions">
            <button type="button" className="erp-btn" onClick={() => onClose(changed)} disabled={busy}>{t("Close")}</button>
            {canCancel && !confirming && <button type="button" className="erp-btn erp-btn--danger" onClick={() => { setConfirming(true); setNotice(""); }} disabled={!selectedIds.length || loading || busy}>{t("Cancel selected quantities")}</button>}
          </div>
        </footer>
      </section>
    </div>
  );
}
